import State from 'store/State';
import { PortfolioInput } from 'portfolio/PortfolioInput';
import { selectAllOrders } from 'app/store/orders/selectors';

import { initialPortfolioItem, PortfolioItem } from 'portfolio/PortfolioItem';
import { mapOrdersToPortfolioItems } from 'app/store/orders/mapping';
import { getPortfolioColumns } from 'app/store/orders/portfolioColumns';
import Order from 'communication/types/order/Order';
import {
    buildPortfolioSelection,
    PortfolioSelection,
} from 'portfolio/PortfolioSelection';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import {
    selectEmbeddedAppOrderIds,
    selectEmbeddedAppPositions,
    selectEmbeddedAppState,
} from 'embedded/store/embeddedAppStateSlice';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

/*
 * The PortfolioInput implementation for Embedded Apps (such as Newport)
 */
export const EmbeddedAppPortfolioInput: PortfolioInput = {
    getFilteredOrders,
    getFilteredPortfolio,
    usesOrdersStream,
    getPortfolioColumns,
    getStreamingOrderIds,
    getSelection: getEmbeddedAppPortfolioSelection,
};

function usesOrdersStream(state: State): boolean {
    const embeddedAppState = selectEmbeddedAppState(state);
    return !!embeddedAppState.orderIds;
}

/*
 * For Embedded Apps (such as Newport), the filtered portfolio is the complete set of streamed orders or positions, or empty if there are none
 */
function getFilteredPortfolio(state: State): PortfolioItem[] {
    const embeddedAppState = selectEmbeddedAppState(state);

    if (embeddedAppState.orderIds && embeddedAppState.orderIds.length > 0) {
        const orders = selectAllOrders(state);
        return mapOrdersToPortfolioItems(state, orders);
    }

    if (
        embeddedAppState.positions &&
        embeddedAppState.positions.values &&
        embeddedAppState.positions.values.length > 0
    ) {
        return embeddedAppState.positions.values.map((p) => ({
            ...initialPortfolioItem,
            ...p,
        }));
    }

    return [];
}

function getFilteredOrders(state: State): Order[] {
    const embeddedAppState = selectEmbeddedAppState(state);

    if (embeddedAppState.orderIds && embeddedAppState.orderIds.length > 0) {
        return selectAllOrders(state);
    }

    return [];
}

function getStreamingOrderIds(state: State): string[] {
    const embeddedAppState = selectEmbeddedAppState(state);
    return embeddedAppState.orderIds ?? [];
}

/*
 * For embedded ap[s such as New[prt, the selection is all of the streamed orders when an _o parameter was received, or the positions from the port parameter
 */
export function getEmbeddedAppPortfolioSelection(
    state: State
): PortfolioSelection {
    const { combineSellsAndShorts, symbology } = selectAppSettings(state);
    const orderIds = selectEmbeddedAppOrderIds(state);
    const positions = selectEmbeddedAppPositions(state);

    if (orderIds && orderIds.length > 0) {
        // Selection is all the stream orders, with the symbology from AppSettings...
        const selectedOrders = selectAllOrders(state);
        return buildPortfolioSelection({
            selectedOrders,
            symbology,
            combineSellsAndShorts,
        });
    }

    if (positions) {
        // Selection is the receive port parameter, with the supplied symbology or if none was supplied "Instinet"
        return buildPortfolioSelection({
            selectedPositions: positions.values,
            symbology: positions.symbology ?? SymbologyValues.Instinet,
            combineSellsAndShorts,
        });
    }

    return buildPortfolioSelection({ symbology });
}
